const LS_KEY = 'stormsurge-tutorial-v1';

type Binding = { keys: string; action: string };

const BINDINGS: Binding[] = [
  { keys: 'W A S D', action: 'Move after you land' },
  { keys: 'Shift / C', action: 'Sprint / slide' },
  { keys: 'Space', action: 'Jump / deploy glider while falling' },
  { keys: 'E', action: 'Open chests and pick up loot' },
  { keys: '1 - 3', action: 'Swap weapons' },
  { keys: 'LMB / RMB', action: 'Fire / aim' },
  { keys: '4 / B', action: 'Build cover' },
  { keys: 'Tab', action: 'Pickaxe for mats' },
  { keys: 'G', action: 'Ping a drop or danger' },
  { keys: 'R', action: 'Reload / reboot' },
];

/**
 * Skippable controls overlay — shown once per browser until dismissed.
 */
export class FpsTutorial {
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private visible = false;

  constructor(
    mount: HTMLElement,
    private readonly onSkip?: () => void,
  ) {
    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:120',
      'display:none',
      'align-items:center',
      'justify-content:center',
      'background:rgba(6,8,14,0.62)',
      'backdrop-filter:blur(10px)',
      '-webkit-backdrop-filter:blur(10px)',
      'pointer-events:auto',
    ].join(';');

    this.panel = document.createElement('div');
    this.panel.style.cssText = [
      'min-width:min(92vw,420px)',
      'max-width:480px',
      'padding:22px 24px 20px',
      'border-radius:14px',
      'background:linear-gradient(165deg,rgba(24,32,52,0.96),rgba(12,16,28,0.98))',
      'border:2px solid rgba(46,230,255,0.65)',
      'box-shadow:0 8px 32px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.06)',
      'color:#e8f4ff',
      'font:14px system-ui,Segoe UI,sans-serif',
      'pointer-events:auto',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'First Drop Controls';
    title.style.cssText =
      'font-weight:700;font-size:16px;letter-spacing:0.06em;margin-bottom:4px;color:#2ee6ff';
    this.panel.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.textContent = 'Drop in, deploy your glider, loot fast, build cover, and survive.';
    subtitle.style.cssText = 'font-size:12px;opacity:0.72;margin-bottom:16px';
    this.panel.appendChild(subtitle);

    const grid = document.createElement('div');
    grid.style.cssText =
      'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 14px;margin-bottom:18px';
    for (const b of BINDINGS) {
      const cell = document.createElement('div');
      cell.style.cssText = [
        'display:flex',
        'align-items:center',
        'gap:10px',
        'padding:8px 10px',
        'border-radius:9px',
        'background:rgba(8,12,22,0.72)',
        'border:1px solid rgba(80,120,160,0.35)',
      ].join(';');
      const keys = document.createElement('span');
      keys.textContent = b.keys;
      keys.style.cssText =
        'flex:0 0 auto;min-width:52px;font:800 11px ui-monospace,monospace;color:#2ee6ff;letter-spacing:0.04em';
      const action = document.createElement('span');
      action.textContent = b.action;
      action.style.cssText = 'font:600 12px system-ui;color:rgba(232,244,255,0.92)';
      cell.appendChild(keys);
      cell.appendChild(action);
      grid.appendChild(cell);
    }
    this.panel.appendChild(grid);

    const flow = document.createElement('div');
    flow.textContent = 'Plan: click to jump from the Battle Bus, press Space to deploy if falling, then find a chest or weapon.';
    flow.style.cssText = [
      'margin:-4px 0 16px',
      'padding:10px 12px',
      'border-radius:10px',
      'background:rgba(46,230,255,0.1)',
      'border:1px solid rgba(46,230,255,0.24)',
      'color:rgba(232,244,255,0.9)',
      'font:700 12px system-ui,sans-serif',
      'line-height:1.4',
    ].join(';');
    this.panel.appendChild(flow);

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap';

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.textContent = 'Skip';
    skipBtn.style.cssText = [
      'padding:9px 16px',
      'border-radius:8px',
      'border:1px solid rgba(120,150,190,0.55)',
      'background:rgba(20,28,44,0.9)',
      'color:#c8d8f0',
      'font:700 12px system-ui,sans-serif',
      'cursor:pointer',
      'letter-spacing:0.04em',
    ].join(';');
    skipBtn.addEventListener('click', () => {
      this.onSkip?.();
      this.hide();
    });

    const dismissBtn = document.createElement('button');
    dismissBtn.type = 'button';
    dismissBtn.textContent = "Don't show again";
    dismissBtn.style.cssText = [
      'padding:9px 16px',
      'border-radius:8px',
      'border:1px solid rgba(46,230,255,0.55)',
      'background:linear-gradient(180deg,rgba(30,90,120,0.85),rgba(18,50,72,0.92))',
      'color:#e8f4ff',
      'font:700 12px system-ui,sans-serif',
      'cursor:pointer',
      'letter-spacing:0.04em',
    ].join(';');
    dismissBtn.addEventListener('click', () => {
      localStorage.setItem(LS_KEY, '1');
      this.hide();
    });

    actions.appendChild(skipBtn);
    actions.appendChild(dismissBtn);
    this.panel.appendChild(actions);

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);

    const stop = (e: Event): void => {
      e.stopPropagation();
    };
    for (const evt of ['pointerdown', 'mousedown', 'click', 'wheel']) {
      this.backdrop.addEventListener(evt, stop, { passive: evt === 'wheel' });
      this.panel.addEventListener(evt, stop, { passive: evt === 'wheel' });
    }
  }

  shouldShow(): boolean {
    return localStorage.getItem(LS_KEY) === null;
  }

  show(): void {
    if (!this.shouldShow()) return;
    this.visible = true;
    this.backdrop.style.display = 'flex';
  }

  hide(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.backdrop.remove();
  }
}
